import { ReactElement, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import unlockAcademics from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_academics.png';
import unlockAcademicsFull from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_academics_full.png';
import unlockAchievement from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_achievement.png';
import unlockAchievementFull from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_achievement_full.png';
import unlockCommunities from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_communities.png';
import unlockCommunitiesFull from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_communities_full.png';
import unlockSkills from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_skills.png';
import unlockSkillsFull from '@backrs_ui/common-ui/src/assets/images/unlocks/unlock_skills_full.png';
import { ActionTabs } from '@backrs_ui/common-ui/src/atoms/ActionTabs';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import {
  TrackAssessmentCategory,
  StandardsVariant,
} from '@backrs_ui/common-ui/src/models/trackAssessment';
import { StandardsByTrackModal } from '@backrs_ui/common-ui/src/molecules/AssessMyTrack/StandardsByTrackModal';
import {
  BASE_NUGGET_HEIGHT,
  BASE_NUGGET_WIDTH,
} from '@backrs_ui/common-ui/src/molecules/Nugget/BaseNugget';
import { TypeChip } from '@backrs_ui/common-ui/src/molecules/Nugget/TypeChip';

import { Box, Typography, SxProps, Theme, Button } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const UNLOCK_STRINGS = appStrings.modules.nugget.unlockCard;
const ACTION_TAB_STRINGS = appStrings.modules.actionTabs;

const CATEGORY_IMAGES: Record<TrackAssessmentCategory['slug'], string> = {
  'academic-foundations': unlockAcademics,
  'real-world-experience': unlockAchievement,
  'mindsets-and-skills': unlockSkills,
  'community-of-success': unlockCommunities,
  none: '',
};

const CATEGORY_IMAGES_FULL: Record<TrackAssessmentCategory['slug'], string> = {
  'academic-foundations': unlockAcademicsFull,
  'real-world-experience': unlockAchievementFull,
  'mindsets-and-skills': unlockSkillsFull,
  'community-of-success': unlockCommunitiesFull,
  none: '',
};

const getCardContainerSx = (variant: 'TINY' | 'SMALL' | 'LARGE'): SxProps<Theme> => {
  return cardContainerStyles[variant];
};

const ReactMarkdownWithTypography = ({ children }: { children: string }) => {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => (
          <Typography
            variant="text-sm-regular"
            sx={{ color: ({ backrs }) => backrs.colors['Colors/Text/text-tertiary'] }}
          >
            {children}
          </Typography>
        ),
        li: ({ children }) => (
          <li>
            <Typography
              variant="text-sm-regular"
              sx={{ color: ({ backrs }) => backrs.colors['Colors/Text/text-tertiary'] }}
            >
              {children}
            </Typography>
          </li>
        ),
        ul: ({ children }) => <ul style={ulStyle}>{children}</ul>,
      }}
    >
      {children}
    </ReactMarkdown>
  );
};

type CategoryCardProps = Readonly<{
  id?: string;
  variant:
    | {
        type: 'TINY';
        category: TrackAssessmentCategory;
      }
    | {
        type: 'SMALL';
        category: TrackAssessmentCategory;
        strengths: string[];
        opportunities: string[];
        onClick?: () => void;
        hideContent?: boolean;
      }
    | {
        type: 'LARGE';
        category: TrackAssessmentCategory;
        strengths: string[];
        opportunities: string[];
        blockAssessmentText?: string;
        onBlockAssessmentButtonClick: () => void;
        onBookmark: () => void;
        onShare: () => void;
        onAddTask: () => void;
        disableButtons: boolean;
        hasBookmarked?: boolean;
        hideContent?: boolean;
        standardsVariant?: StandardsVariant;
      };
}>;

type BlockedAssessmentContentProps = Readonly<{
  text: string;
  onBlockAssessmentButtonClick: () => void;
  categorySlug?: string;
}>;

const BlockedAssessmentContent = ({
  text,
  onBlockAssessmentButtonClick,
  categorySlug,
}: BlockedAssessmentContentProps) => {
  return (
    <>
      <Box sx={customDividerSx}>
        <Box sx={leftLineSx} />
        <Typography sx={dividerTextSx}>{UNLOCK_STRINGS.strengthsAndOpportunities}</Typography>
        <Box sx={rightLineSx} />
      </Box>
      <Box sx={blockedContentItemSx}>
        <Box sx={blockedIconContainerSx}>
          <AppIcon iconName="Target03" diameter="32px" color="Colors/Foreground/fg-brand-primary" />
        </Box>
        <ReactMarkdownWithTypography>{text}</ReactMarkdownWithTypography>
      </Box>
      <Button
        hierarchy="Secondary gray"
        size="md"
        sx={uploadTranscriptButtonSx}
        onClick={onBlockAssessmentButtonClick}
        id={categorySlug ? `assess-track-upload-transcript-${categorySlug}` : undefined}
      >
        {UNLOCK_STRINGS.uploadYourTranscript}
      </Button>
    </>
  );
};

const LargeVariantContent = ({
  variant,
}: {
  variant: Extract<CategoryCardProps['variant'], { type: 'LARGE' }>;
}) => {
  return (
    <>
      {/* Strengths */}
      {variant.strengths.length > 0 && (
        <>
          <Box sx={customDividerSx}>
            <Box sx={leftLineSx} />
            <Typography sx={dividerTextSx}>{UNLOCK_STRINGS.strengths}</Typography>
            <Box sx={rightLineSx} />
          </Box>
          {variant.strengths.map((item, index) => (
            <Box key={`strength-${index}`}>
              <Box sx={contentItemSx}>
                <Box sx={iconContainerSx}>
                  <AppIcon iconName="CheckCircle" diameter="32px" color="Green/500" />
                </Box>
                <ReactMarkdownWithTypography>{item}</ReactMarkdownWithTypography>
              </Box>
              {(index < variant.strengths.length - 1 || variant.opportunities.length === 0) && (
                <Box sx={dividerSx} />
              )}
            </Box>
          ))}
        </>
      )}

      {/* Opportunities */}
      {variant.opportunities.length > 0 && (
        <>
          <Box sx={customDividerSx}>
            <Box sx={leftLineSx} />
            <Typography sx={dividerTextSx}>{UNLOCK_STRINGS.opportunitiesToGrow}</Typography>
            <Box sx={rightLineSx} />
          </Box>
          {variant.opportunities.map((item, index) => (
            <Box key={`opportunity-${index}`}>
              <Box sx={contentItemSx}>
                <Box sx={iconContainerSx}>
                  <AppIcon
                    iconName="Target03"
                    diameter="32px"
                    color="Colors/Foreground/fg-brand-primary"
                  />
                </Box>
                <ReactMarkdownWithTypography>{item}</ReactMarkdownWithTypography>
              </Box>
              <Box sx={dividerSx} />
            </Box>
          ))}
        </>
      )}
    </>
  );
};

const TinyUnlockCard = ({
  variant,
}: {
  variant: Extract<CategoryCardProps['variant'], { type: 'TINY' }>;
}) => {
  return (
    <Box sx={getCardContainerSx('TINY')}>
      <Box sx={tinyVariantHeaderSx}>
        <Box
          component="img"
          src={CATEGORY_IMAGES_FULL[variant.category.slug]}
          alt={`Unlock ${variant.category.name}`}
          sx={tinyVariantUnlockImageSx}
        />
      </Box>

      <Box sx={tinyVariantCardBodySx}>
        <Box sx={tinyContentContainerSx}>
          <Typography variant="text-sm-medium" sx={descriptionTextSx}>
            <strong>{variant.category.name}</strong>: {variant.category.description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const SmallUnlockCard = ({
  variant,
}: {
  variant: Extract<CategoryCardProps['variant'], { type: 'SMALL' }>;
}) => {
  return (
    <Box sx={getCardContainerSx('SMALL')} onClick={variant.onClick}>
      <Box sx={headerSx}>
        <Box
          component="img"
          src={CATEGORY_IMAGES[variant.category.slug]}
          alt={`Unlock ${variant.category.name}`}
          sx={unlockImageSx}
        />
      </Box>
      {!variant.hideContent && (
        <Box sx={cardBodySx}>
          <Box sx={titleRowSx}>
            <Box sx={titleAndDescriptionSx}>
              <Typography variant="text-xl-semibold" sx={titleSmallVariantSx}>
                {variant.category.name}{' '}
                <Box component="span" sx={infoIconContainerSx}>
                  <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Base/Black" />
                </Box>
              </Typography>
              <Typography variant="text-xs-medium" sx={descriptionTextSx}>
                {variant.category.description}
              </Typography>
            </Box>
          </Box>
          <Box sx={smallContentContainerSx}>
            <Box sx={smallContentItemsSx}>
              {[...variant.strengths, ...variant.opportunities].slice(0, 2).map((item, index) => (
                <Box key={index}>
                  <Box sx={dividerSx} />
                  <Box sx={smallContentTextSx}>
                    <ReactMarkdownWithTypography>{item}</ReactMarkdownWithTypography>
                  </Box>
                </Box>
              ))}
            </Box>
            <TypeChip label={UNLOCK_STRINGS.fourUnlocks} icon="LockKeyholeSquare" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const LargeUnlockCard = ({
  variant,
}: {
  variant: Extract<CategoryCardProps['variant'], { type: 'LARGE' }>;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  return (
    <Box sx={getCardContainerSx('LARGE')}>
      <Box sx={headerSx}>
        <Box
          component="img"
          src={CATEGORY_IMAGES[variant.category.slug]}
          alt={`Unlock ${variant.category.name}`}
          sx={unlockImageSx}
        />
      </Box>
      {!variant.hideContent && (
        <Box sx={cardBodySx}>
          <Box sx={titleRowSx}>
            <Box sx={titleAndDescriptionSx}>
              <Typography variant="text-xl-semibold" sx={titleLargeVariantSx}>
                {variant.category.name}{' '}
                <Box
                  component="span"
                  sx={infoIconContainerSx}
                  onClick={() => setIsModalOpen(true)}
                  id={`assess-track-unlock-info-${variant.category.slug}`}
                >
                  <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Base/Black" />
                </Box>
              </Typography>
              <Typography variant="text-xs-medium" sx={descriptionTextSx}>
                {variant.category.description}
              </Typography>
            </Box>
            <Box sx={actionTabsContainerSx}>
              <ActionTabs
                actions={[
                  {
                    icon: variant.hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
                    onClick: variant.onBookmark,
                    id: `assess-track-unlock-bookmark-${variant.category.slug}`,
                    tooltipHoverText: ACTION_TAB_STRINGS.bookmark,
                  },
                  {
                    icon: 'Share06',
                    onClick: variant.onShare,
                    id: `assess-track-unlock-share-${variant.category.slug}`,
                    tooltipHoverText: ACTION_TAB_STRINGS.share,
                  },
                  {
                    icon: 'BookmarkAdd',
                    onClick: variant.onAddTask,
                    id: `assess-track-unlock-add-task-${variant.category.slug}`,
                    tooltipHoverText: ACTION_TAB_STRINGS.task,
                  },
                ]}
                showCopySuccess={showCopySuccess}
                onCopySuccessChange={setShowCopySuccess}
                borderColor="Colors/Border/border-primary"
                disabled={variant.disableButtons || !!variant.blockAssessmentText}
              />
            </Box>
          </Box>

          {variant.blockAssessmentText ? (
            <BlockedAssessmentContent
              text={variant.blockAssessmentText}
              onBlockAssessmentButtonClick={variant.onBlockAssessmentButtonClick}
              categorySlug={variant.category.slug}
            />
          ) : (
            <LargeVariantContent variant={variant} />
          )}
        </Box>
      )}
      {variant.standardsVariant && (
        <StandardsByTrackModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          category={variant.category}
          variant={variant.standardsVariant}
        />
      )}
    </Box>
  );
};

export const UnlockCard = ({ id, variant }: CategoryCardProps): ReactElement => {
  const inner = (() => {
    switch (variant.type) {
      case 'TINY':
        return <TinyUnlockCard variant={variant} />;
      case 'SMALL':
        return <SmallUnlockCard variant={variant} />;
      case 'LARGE':
        return <LargeUnlockCard variant={variant} />;
      default:
        return (
          <TinyUnlockCard
            variant={variant as Extract<CategoryCardProps['variant'], { type: 'TINY' }>}
          />
        );
    }
  })();

  if (id) {
    return <Box id={id}>{inner}</Box>;
  }

  return inner;
};

const tinyCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
  height: '100%',
  backgroundColor: backrs.colors['Colors/Base/White'],
  minWidth: '307px',
  minHeight: '219px',
});

const smallCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-4xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
  height: '100%',
  backgroundColor: backrs.colors['Gray/700'],
  maxWidth: BASE_NUGGET_WIDTH,
  maxHeight: BASE_NUGGET_HEIGHT,
  cursor: 'pointer',
  minHeight: BASE_NUGGET_HEIGHT,
});

const largeCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  overflow: 'hidden',
  height: '100%',
  backgroundColor: backrs.colors['Gray/700'],
});

const cardContainerStyles = {
  TINY: tinyCardContainerSx,
  SMALL: smallCardContainerSx,
  LARGE: largeCardContainerSx,
} as const;

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '72px',
  backgroundColor: backrs.colors['Gray/700'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  position: 'relative',
});

const unlockImageSx: SxProps<Theme> = {
  height: '100%',
  width: 'auto',
  objectFit: 'contain',
  maxWidth: '100%',
};

const cardBodySx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: `${backrs.radius['radius-xl']} ${backrs.radius['radius-xl']} 0px 0px`,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: {
    xs: 'column',
    sm: 'row',
  },
  columnGap: backrs.spacing['spacing-lg'],
  justifyContent: 'space-between',
  alignItems: {
    xs: 'flex-end',
    sm: 'flex-start',
  },
  marginBottom: backrs.spacing['spacing-sm'],
});

const blockedContentItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  paddingTop: backrs.spacing['spacing-4xl'],
  paddingBottom: backrs.spacing['spacing-4xl'],
});

const contentItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  paddingTop: backrs.spacing['spacing-md'],
  paddingBottom: backrs.spacing['spacing-md'],
});

const blockedIconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  flexShrink: 0,
  marginRight: backrs.spacing['spacing-4xl'],
  marginLeft: backrs.spacing['spacing-2xl'],
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  flexShrink: 0,
  marginRight: backrs.spacing['spacing-lg'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  backgroundColor: backrs.colors['Gray/300'],
  marginTop: backrs.spacing['spacing-sm'],
  marginBottom: backrs.spacing['spacing-sm'],
  marginLeft: `-${backrs.spacing['spacing-2xl']}`,
  marginRight: `-${backrs.spacing['spacing-2xl']}`,
});

const customDividerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: backrs.spacing['spacing-md'],
  marginLeft: `-${backrs.spacing['spacing-2xl']}`,
  marginRight: `-${backrs.spacing['spacing-2xl']}`,
});

const leftLineSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  width: '48px',
  backgroundColor: backrs.colors['Gray/300'],
});

const rightLineSx: SxProps<Theme> = ({ backrs }) => ({
  height: '1px',
  flex: 1,
  backgroundColor: backrs.colors['Gray/300'],
});

const dividerTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  fontSize: '14px',
  fontWeight: 600,
  backgroundColor: backrs.colors['Colors/Base/White'],
  paddingLeft: backrs.spacing['spacing-xl'],
  paddingRight: backrs.spacing['spacing-xl'],
  marginLeft: backrs.spacing['spacing-sm'],
  marginRight: backrs.spacing['spacing-sm'],
});

const titleAndDescriptionSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

const infoIconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  verticalAlign: 'middle',
  cursor: 'pointer',
  mb: backrs.spacing['spacing-xs'],
  ml: backrs.spacing['spacing-xs'],
});

const tinyContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: backrs.spacing['spacing-sm'],
  flex: 1,
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  textAlign: 'left',
});

const tinyVariantHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  height: '110px',
  backgroundColor: '#E8F0FF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  borderBottom: `1px solid ${backrs.colors['Gray/900']}`,
  overflow: 'hidden',
});

const tinyVariantUnlockImageSx: SxProps<Theme> = {
  height: '115%',
  width: 'auto',
  objectFit: 'contain',
  maxWidth: '105%',
  transform: 'scale(1.15)',
};

const tinyVariantCardBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: backrs.spacing['spacing-lg'],
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
});

const smallContentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  marginTop: backrs.spacing['spacing-md'],
  flex: 1,
});

const smallContentItemsSx: SxProps<Theme> = {
  flex: 1,
};

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const smallContentTextSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  lineHeight: '1.4',
  color: backrs.colors['Colors/Text/text-tertiary'],
  marginBottom: backrs.spacing['spacing-md'],
  marginTop: backrs.spacing['spacing-md'],
});

const titleSmallVariantSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  width: '100%',
};

const titleLargeVariantSx: SxProps<Theme> = {
  display: 'inline',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const actionTabsContainerSx: SxProps<Theme> = {
  order: {
    xs: -1,
    sm: 0,
  },
};

const ulStyle: React.CSSProperties = {
  marginTop: '0',
  marginLeft: '1em',
  paddingLeft: '0.5em',
};

const uploadTranscriptButtonSx: SxProps<Theme> = {
  width: {
    xs: '100%',
    sm: '350px',
  },
  alignSelf: 'center',
};
